<script lang="ts">
  import { untrack, onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import { t } from '$core/i18n';
  import {
    listNodes,
    createNode,
    deleteNodeCascade,
    listAttemptsByUser,
    listAllDeletedNodes,
    restoreNodeCascade,
    hardDeleteNodeCascade
  } from '$db/local/repositories';
  import SubjectSettingsModal from '$ui/components/SubjectSettingsModal.svelte';
  import SessionContextFields from '$ui/components/SessionContextFields.svelte';
  import type { Node, ProblemAttempt } from '$domain/types';
  import type { SessionContext, SessionStore } from '$features/session';
  import type { PreferencesStore } from '$core/preferences';
  import ReviewGroupsList from '$ui/components/ReviewGroupsList.svelte';
  import RecentProblems from '$ui/components/RecentProblems.svelte';
  import { Button } from '$ui/primitives/Button';
  import { Icon } from '$ui/primitives/Icon';

  import { syncStore } from '$features/sync';
  import { toast } from '$ui/primitives/Toast';

  let {
    store,
    prefs,
    disabled = false,
    onNavigate
  }: {
    store: SessionStore;
    prefs: PreferencesStore;
    disabled?: boolean;
    onNavigate?: (view: 'checklist') => void;
  } = $props();

  // --- Contexto local (mirror de prefs.lastContext) ---
  // Inicialización SINCRÓNICA con `untrack` — captura el valor actual de
  // preferencias en el primer render (evita el flash de "Choose a...").
  function clampInput(val: any, min: number, max: number) {
    const num = Number(val);
    if (isNaN(num) || num < min) return min;
    if (num > max) return max;
    return Math.floor(num);
  }
  let localContext = $state<SessionContext | null>(
    untrack(() => {
      const ctx = prefs.prefs.lastContext;
      return ctx && ctx.nodeId
        ? { nodeId: ctx.nodeId, path: ctx.path }
        : null;
    })
  );

  // Fallback: si por alguna razón el init corrió con prefs vacío, este
  // effect hidrata cuando las preferencias se pueblan.
  let isUserCleared = $state(false);

  $effect(() => {
    if (localContext !== null || isUserCleared) return;
    const ctx = prefs.prefs.lastContext;
    if (!ctx || !ctx.nodeId) return;
    localContext = { nodeId: ctx.nodeId, path: ctx.path };
  });

  function handleContextChange(patch: Partial<SessionContext>) {
    isUserCleared = false;
    const cur = localContext;
    const next: SessionContext = {
      nodeId: patch.nodeId !== undefined ? patch.nodeId : (cur?.nodeId ?? null),
      path: patch.path !== undefined ? patch.path : (cur?.path ?? [])
    };
    localContext = next;
    if (next.nodeId) {
      prefs.setLastContext({
        nodeId: next.nodeId,
        path: next.path
      });
    } else {
      prefs.prefs.lastContext = null;
    }
  }

  let canStart = $derived(!!localContext?.nodeId);

  // Modo Recall: la card izquierda se convierte en lista de grupos o en el
  // item activo. El resto del setup (subject chain) se oculta — no aplica.
  // El Pomodoro a la derecha sigue funcionando normalmente.
  let isReviewMode = $derived(store.mode === 'B');

  // En modo Recall, "Start focus" requiere un grupo seleccionado (el contexto
  // viene del primer item del batch, no del setup de Subject chain).
  // En modo Practice, requiere solo subjectId.
  let canStartBlock = $derived(
    !disabled && (isReviewMode ? store.selectedGroupKey !== null : canStart)
  );



  let dueCount = $state(0);



  type DurationChoice = '25' | '50' | '90' | 'manual' | 'open';
  let durationChoice: DurationChoice = $state('25');
  let manualMinutes: number = $state(45);
  let freeTimer = $state(false);
  const PRESET_CHOICES: DurationChoice[] = ['25', '50', '90'];
  const BREAK_FOR: Record<'25' | '50' | '90', number> = { '25': 5, '50': 10, '90': 20 };
  let breakMinutes: number = $state(5);

  let checklistItems = $state<Node[]>([]);
  let allAttempts = $state<ProblemAttempt[]>([]);

  let hasAttemptsForNode = $derived(
    !!localContext?.nodeId && allAttempts.some(a => a.nodeId === localContext!.nodeId)
  );

  let isAddingTopic = $state(false);
  let newTopicName = $state('');
  let showTrash = $state(false);
  let nodeToDelete = $state<Node | null>(null);
  let archivedNodes = $state<Node[]>([]);
  let archivedRoots = $derived(archivedNodes.filter(n => !n.parentId));
  let archivedTree = $derived.by(() => {
    const map = new Map<string, Node[]>();
    for (const item of archivedNodes) {
      if (item.id === item.parentId) continue;
      const pId = item.parentId || 'root';
      if (!map.has(pId)) map.set(pId, []);
      map.get(pId)!.push(item);
    }
    return map;
  });

  async function openTrash() {
    showTrash = true;
    archivedNodes = await listAllDeletedNodes();
  }

  async function restoreFromTrash(id: string) {
    await restoreNodeCascade(id);
    await openTrash(); // Recargar la lista de borrados
    checklistItems = await listNodes(); // Recargar los nodos activos
  }

  async function permanentlyDeleteFromTrash(id: string) {
    if (!confirm(t('checklist.deleteConfirm'))) return;
    await hardDeleteNodeCascade(id);
    await openTrash();
  }


  // Tree logic
  let expanded = $state(new Set<string>());
  let searchQuery = $state('');
  let addingChildTo = $state<Node | null>(null);
  let newChildName = $state('');
  let showSubjectSettings = $state(false);

  let tree = $derived.by(() => {
    const map = new Map<string, Node[]>();
    for (const item of checklistItems) {
      // Skip self-references (subjects with parentId === id)
      if (item.id === item.parentId) continue;
      if (prefs.prefs.hideCompleted && item.completed) continue;
      const pId = item.parentId || 'root';
      if (!map.has(pId)) map.set(pId, []);
      map.get(pId)!.push(item);
    }
    return map;
  });

  let flatTree = $derived.by(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return checklistItems.filter(i => i.title.toLowerCase().includes(q));
  });

  // Un nodo es raíz si no tiene parentId (null/undefined) O si es
  // auto-referenciado (parentId === id), que es como la DB puede
  // guardar los subjects en algunas versiones.
  let rootItems = $derived(
    checklistItems.filter(i =>
      (!i.parentId || i.parentId === i.id) &&
      (!prefs.prefs.hideCompleted || !i.completed)
    )
  );

  function getNextLevel(type: string) {
    if (type === 'subject') return 'Subtopic';
    if (type === 'subtopic') return 'Book';
    if (type === 'book') return 'Chapter';
    return 'Section';
  }

  function toggleExpand(id: string) {
    if (expanded.has(id)) expanded.delete(id);
    else expanded.add(id);
    expanded = new Set(expanded);
  }

  function startAdding(parent: Node) {
    addingChildTo = parent;
    if (!expanded.has(parent.id)) {
      expanded.add(parent.id);
      expanded = new Set(expanded);
    }
  }

  async function saveChild(parent: Node) {
    if (!newChildName.trim()) return;
    try {
      const nextType = getNextLevel(parent.type);
      await createNode(parent.id, newChildName.trim(), nextType);
      checklistItems = await listNodes();
      store.dbMutationVersion += 1;
      newChildName = '';
      addingChildTo = null;
    } catch (e) {
      console.error(e);
      toast.error("Failed to create item");
    }
  }

  function requestDeleteNode(item: Node) {
    nodeToDelete = item;
  }

  async function confirmDeleteNode() {
    if (!nodeToDelete) return;
    
    const itemToDelete = nodeToDelete;
    nodeToDelete = null;

    // 1. UI Optimista: Lo quitamos de la pantalla de inmediato para que se sienta instantáneo
    const idsToRemove = new Set([itemToDelete.id]);
    function collectChildren(parentId: string) {
      for (const item of checklistItems) {
        // 1. Evitar la auto-referencia infinita (Subjects apuntan a sí mismos)
        if (item.id === item.parentId) continue;
        
        // 2. Si ya lo agregamos, lo saltamos para evitar ciclos
        if (idsToRemove.has(item.id)) continue;

        if (item.parentId === parentId) {
          idsToRemove.add(item.id);
          collectChildren(item.id);
        }
      }
    }
    collectChildren(itemToDelete.id);
    checklistItems = checklistItems.filter(i => !idsToRemove.has(i.id));

    // 2. Borrado real en la base de datos
    await deleteNodeCascade(itemToDelete.id);
    
    // 3. Sincronizamos por si acaso
    checklistItems = await listNodes();
    store.dbMutationVersion += 1;
  }

  $effect(() => {
    void store.dbMutationVersion;
    void syncStore.running; // Fuerza recarga de la UI cuando termina la sincronización
    void syncStore.hasPending;
    void (async () => {
      checklistItems = await listNodes();
      allAttempts = await listAttemptsByUser();
    })();
  });

  function selectTopic(item: Node) {
    const path: string[] = [];
    let current: Node | undefined = item;
    
    while (current) {
      path.unshift(current.id);
      if (!current.parentId) break;
      current = checklistItems.find(i => i.id === current!.parentId);
    }

    handleContextChange({ nodeId: item.id, path });
  }

  onMount(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      
      if (e.key === 'Escape') {
        isUserCleared = true;
        localContext = null;
        prefs.prefs.lastContext = null;
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopPropagation(); // Evita que el atajo global de App.svelte inicie la sesión
        startBlock();
      }
    };
    window.addEventListener('keydown', handleKeydown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeydown, { capture: true });
  });


  async function addTopic() {
    if (!newTopicName.trim()) return;
    const newSubj = await createNode(null, newTopicName.trim(), 'subject');
    checklistItems = await listNodes();
    newTopicName = '';
    isAddingTopic = false;
    store.dbMutationVersion += 1;
    handleContextChange({ nodeId: newSubj.id, path: [newSubj.id] });
  }

  function pickPreset(c: DurationChoice) {
    freeTimer = false;
    durationChoice = c;
    breakMinutes = BREAK_FOR[c as '25' | '50' | '90'];
  }

  async function refreshDueCount() {
    dueCount = await store.loadDueRecallItems();
  }

  $effect(() => {
    void store.mode;
    void store.macro.state;
    void store.dbMutationVersion;
    void (async () => {
      await refreshDueCount();
    })();
  });

  // Handle mode change side effects
  let lastMode = untrack(() => store.mode);
  $effect(() => {
    if (store.mode === lastMode) return;
    if (store.mode === 'B') {
      void startReview();
    } else {
      store.exitModeB();
    }
    lastMode = store.mode;
  });

  async function startReview() {
    const ok = await store.enterModeB();
    if (!ok) await refreshDueCount();
  }

  function startBlock() {
    if (!isReviewMode && !canStart) {
      toast.error("Please select a subject to start the session.");
      return;
    }
    // En modo Recall el contexto lo aporta el primer item del batch (los
    // items ya llevan su propio subject/subtopic/etc.). En modo Practice
    // usamos el context del setup.
    let ctx: SessionContext | null = null;
    if (isReviewMode) {
      const first = store.reviewBatch[0];
      if (first) {
        ctx = { nodeId: first.nodeId, path: [first.nodeId] };
      }
    } else {
      ctx = localContext;
    }

    if (!ctx) return;
    if (!isReviewMode && !canStart) return;

    if (durationChoice === 'open') {
      store.startSession(ctx, { openEnded: true });
      return;
    }
    const minutes =
      durationChoice === 'manual'
        ? Math.max(1, Math.min(600, Math.floor(manualMinutes)))
        : Number(durationChoice);
    store.startSession(ctx, { focusMs: minutes * 60 * 1000, breakMs: breakMinutes * 60 * 1000 });
  }
</script>

<section class="view" aria-labelledby="session-view-title">
  <header class="view-head">
    <div class="view-head-text">
      <h1 id="session-view-title" class="view-title">{t('shell.nav.session')}</h1>
      <p class="view-subtitle">Set up your study block</p>
    </div>
    {#if syncStore.canSync}
      <span
        class="sync-badge"
        data-state={syncStore.hasError
          ? 'error'
          : syncStore.hasConflicts
            ? 'warning'
            : syncStore.hasPending
              ? 'pending'
              : 'synced'}
        role="button"
        tabindex="0"
        onclick={() => syncStore.syncNow()}
        onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && syncStore.syncNow()}
        title={syncStore.hasError
          ? 'Sync error. Click to retry.'
          : syncStore.hasConflicts
            ? `${syncStore.conflicts} conflicts pending. Click to resolve.`
            : syncStore.hasPending
              ? `${syncStore.pendingOps} ops pending sync. Click to sync now.`
              : 'All synced. Click to force sync.'}
        style="cursor: pointer; user-select: none;"
      >
        {#if syncStore.running}
          Syncing…
        {:else if syncStore.hasError}
          Sync error
        {:else if syncStore.hasConflicts}
          {syncStore.conflicts} conflicts
        {:else if syncStore.hasPending}
          {syncStore.pendingOps} pending
        {:else}
          Synced
        {/if}
      </span>
    {/if}
  </header>

  <!-- 1. Contexto (Qué estudiar) - Oculto por defecto, activable en Preferencias -->
  {#if prefs.prefs.showSessionContextBar}
    <div class="top-setup-bar">
      <div class="context-breadcrumb-wrapper">
        <SessionContextFields
          value={localContext}
          onchange={handleContextChange}
          onMutate={() => { store.dbMutationVersion += 1; }}
          size="sm"
          showAllContext={true}
        />
      </div>
    </div>
  {/if}

  <!-- 3. Timer & Syllabus (Cuándo) -->
  <div class="setup-grid" class:has-recent={hasAttemptsForNode}>
    <div class="setup-right-col">
      
      <!-- 2. Modo (Cómo estudiar) -->
      <div class="mode-container">
        <h3 class="mode-container-title">Study Mode</h3>
        <div class="mode-cards">
          <button class="mode-card" class:active={store.mode === 'A'} onclick={() => store.mode = 'A'}>
            <Icon name="activity" size={20} />
            <div class="mode-info">
              <span class="mode-name">Practice</span>
              <span class="mode-desc">Reinforce concepts with exercises and problems.</span>
            </div>
          </button>
          <button class="mode-card" class:active={store.mode === 'B'} onclick={() => store.mode = 'B'}>
            <Icon name="lightbulb" size={20} />
            <div class="mode-info">
              <span class="mode-name">FSRS Review</span>
              <span class="mode-desc">Spaced repetition based on memory decay.</span>
            </div>
            {#if dueCount > 0}
              <span class="badge-recall">{dueCount} due</span>
            {/if}
          </button>
        </div>
      </div>

      <section class="setup-pomodoro" aria-labelledby="session-pomodoro-title">
      <div class="pomo-header">
        <h2 id="session-pomodoro-title" class="card-title">Pomodoro</h2>
        <div class="timer-mode-toggle">
          <button class:active={!freeTimer} onclick={() => { freeTimer = false; durationChoice = '25'; }}>Countdown</button>
          <button class:active={freeTimer} onclick={() => { freeTimer = true; durationChoice = 'open'; }}>Free Timer</button>
        </div>
      </div>

          <!-- 2. Presets (Cuánto tiempo) -->
          <div class="pomo-presets" class:disabled={freeTimer}>
            {#each PRESET_CHOICES as c (c)}
              <button class="preset-btn" class:active={!freeTimer && durationChoice === c} onclick={() => pickPreset(c)} disabled={freeTimer}>
                {c} / {BREAK_FOR[c as '25' | '50' | '90']}
              </button>
            {/each}
            <button class="preset-btn" class:active={durationChoice === 'manual'} onclick={() => { freeTimer = false; durationChoice = 'manual'; }} disabled={freeTimer}>
              Custom
            </button>
          </div>

          {#if !freeTimer && durationChoice === 'manual'}
            <div class="custom-inputs">
              <label>Focus <input type="number" min="1" max="180" value={manualMinutes} onchange={(e) => { manualMinutes = clampInput(e.currentTarget.value, 1, 180); durationChoice = 'manual'; }}></label>
              <span>/</span>
              <label>Break <input type="number" min="1" max="60" value={breakMinutes} onchange={(e) => { breakMinutes = clampInput(e.currentTarget.value, 1, 60); }}></label>
            </div>
          {/if}

          <!-- 3. Reloj (Timer) -->
          <div class="pomo-center">
            <div class="timer-circle-wrapper">
            <svg class="timer-svg" viewBox="0 0 100 100">
              <circle class="timer-track" cx="50" cy="50" r="46" />
              <circle
                class="timer-progress"
                cx="50" cy="50" r="46"
                stroke-dasharray="289"
                stroke-dashoffset="0"
              />
            </svg>
            <div class="timer-text">
              <div class="timer-value">
                {freeTimer ? '00:00' : `${String(durationChoice === 'manual' ? manualMinutes : Number(durationChoice)).padStart(2, '0')}:00`}
              </div>
              <div class="timer-label">{freeTimer ? 'Free Flow' : 'Focus'}</div>
            </div>
            </div>
          </div>

          <!-- 4. Botón de inicio -->
        <div class="pomo-footer">
          <button class="start-focus-btn" disabled={!canStartBlock} onclick={startBlock}>
            <Icon name="play" size={20} />
            <span>Start Focus</span>
          </button>
          <p class="pomo-subtext">
            {#if freeTimer}
              Open ended session
            {:else}
              <span class="text-white">{durationChoice === 'manual' ? manualMinutes : Number(durationChoice)} min</span> focus · <span class="text-white">{breakMinutes} min</span> break
            {/if}
          </p>
        </div>







        {#if disabled}
          <span class="warn">{t('crossTab.followerBlockedStart')}</span>
        {/if}
        </section>
      </div>

      <div class="setup-left-col">
        <section class="setup-checklist" aria-labelledby="session-setup-title">
          <div class="checklist-header">
            <div>
              <h3 class="checklist-header-title">Syllabus</h3>
              <p class="view-subtitle">Track your progress from Subject to Section.</p>
            </div>
            {#if onNavigate}
              <button
                type="button"
                class="expand-btn"
                title="Open full syllabus"
                aria-label="Open full syllabus"
                onclick={() => onNavigate('checklist')}
              >
                <Icon name="external-link" size={18} />
              </button>
            {/if}
          </div>
          {#if isReviewMode}
            <ReviewGroupsList {store} />
            <div class="field-row review-actions">
              <Button variant="plain" size="md" onclick={() => store.exitModeB()}>
                {t('modeB.exit')}
              </Button>
            </div>
          {:else}
            <div class="search-bar">
              <Icon name="search" size={16} />
              <input type="text" bind:value={searchQuery} placeholder="Search topics..." />
            </div>

            <div class="checklist-list">
              {#if flatTree}
                {#each flatTree as item (item.id)}
                  <div class="tree-item-row" class:selected={localContext?.nodeId === item.id} role="button" tabindex="0" onclick={() => selectTopic(item)} onkeydown={(e) => e.key === 'Enter' && selectTopic(item)}>
                    <span class="level-badge">{item.type}</span>
                    <span class="item-title">{item.title}</span>
                    <div class="tree-actions">
                      <button title="Delete" onclick={(e) => { e.stopPropagation(); requestDeleteNode(item); }}><Icon name="trash" size={14} /></button>
                    </div>
                  </div>
                {/each}
              {:else}
                {#each rootItems as item (item.id)}
                  {@render TreeItem(item, 0)}
                {/each}
                {#if rootItems.length === 0}
                  <div class="empty-checklist">
                    <p>Add your first topic to study</p>
                  </div>
                {/if}
                {#if isAddingTopic}
                  <div class="add-child-input">
                    <input type="text" bind:value={newTopicName} placeholder="Subject name" onkeydown={(e) => e.key === 'Enter' && addTopic()} />
                    <button class="check-btn" onclick={addTopic}><Icon name="check" size={14} /></button>
                    <button class="cancel-btn" onclick={() => isAddingTopic = false}><Icon name="x" size={14} /></button>
                  </div>
                {:else}
                  <button class="add-ghost-btn" onclick={() => isAddingTopic = true}>
                    <Icon name="plus" size={14} />
                    <span>Add Subject</span>
                  </button>
                {/if}
              {/if}
            </div>
          {/if}
          <div class="checklist-footer">
            <button class="trash-btn" onclick={openTrash} title="Open trash">
              <Icon name="trash" size={14} />
              <span>Trash</span>
            </button>
          </div>
        </section>
      </div>

      {#if hasAttemptsForNode}
        <div class="setup-recent-col"
             in:fly={{ x: 30, duration: 250, opacity: 0 }}
             out:fly={{ x: 30, duration: 250, opacity: 0 }}>
          <RecentProblems {store} context={localContext} />
        </div>
      {/if}
  </div>

  {#if nodeToDelete}
    <div class="trash-overlay" role="presentation" onclick={() => nodeToDelete = null}>
      <div class="trash-modal" role="dialog" tabindex="-1" aria-modal="true" onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.key === 'Escape' && (nodeToDelete = null)}>
        <div class="trash-modal-header">
          <h3>Delete "{nodeToDelete.title}"?</h3>
          <button class="trash-close" onclick={() => nodeToDelete = null}><Icon name="x" size={16} /></button>
        </div>
        <div class="delete-warning-content">
          <p>This action will move the following to the trash:</p>
          <ul class="delete-tree-preview">
            <li><span class="level-badge">{nodeToDelete.type}</span> {nodeToDelete.title}</li>
            {#if tree.get(nodeToDelete.id)?.length}
              <li class="child-indicator">...and all its nested Subtopics, Books, Chapters, and Sections.</li>
            {/if}
          </ul>
          <p class="delete-note">
            <Icon name="info" size={14} /> <strong>Your solved problems and sessions will be kept</strong> in your history.
          </p>
        </div>
        <div class="trash-modal-actions">
          <Button variant="plain" onclick={() => nodeToDelete = null}>Cancel</Button>
          <Button variant="filled" class="danger-btn" onclick={confirmDeleteNode}>Move to Trash</Button>
        </div>
      </div>
    </div>
  {/if}

  {#if showSubjectSettings && localContext?.nodeId}
    <SubjectSettingsModal
      subjectId={localContext.nodeId}
      subjectName={checklistItems.find(s => s.id === localContext?.nodeId)?.title ?? ''}
      {store}
      onClose={() => (showSubjectSettings = false)}
    />
  {/if}

  {#if showTrash}
    <div class="trash-overlay" role="presentation" onclick={() => showTrash = false}>
      <div
        class="trash-modal"
        role="dialog"
        tabindex="-1"
        aria-modal="true"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => e.key === 'Escape' && (showTrash = false)}
      >
        <div class="trash-modal-header">
          <h3>Trash</h3>
          <button class="trash-close" onclick={() => showTrash = false}>
            <Icon name="x" size={16} />
          </button>
        </div>
        {#if archivedRoots.length === 0}
          <p class="trash-empty">No items in trash</p>
        {:else}
          <p class="trash-note">{t('checklist.trashWarning')}</p>
          <div class="trash-list">
            {#snippet TrashItem(item: Node, depth: number)}
              {@const children = archivedTree.get(item.id) ?? []}
              {@const hasChildren = children.length > 0}
              {@const isExpanded = expanded.has(item.id)}
              {@const levelLabel = item.type.charAt(0).toUpperCase() + item.type.slice(1)}
              {@const iconName = item.type === 'subject' ? 'book' : item.type === 'subtopic' ? 'layers' : item.type === 'book' ? 'book-open' : item.type === 'chapter' ? 'hash' : 'file-text'}

              <div class="tree-node">
                <div class="tree-item-row">
                  {#if hasChildren}
                    <button class="chevron-btn" onclick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}>
                      <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size={14} />
                    </button>
                  {:else if depth > 0}
                    <span class="chevron-spacer"></span>
                  {/if}

                  <span class="level-badge">{levelLabel}</span>
                  <Icon name={iconName} size={14} class="node-icon" />
                  <span class="item-title">{item.title}</span>

                  {#if depth === 0}
                    <div class="tree-actions" style="opacity: 1;">
                      <button class="trash-restore" onclick={() => restoreFromTrash(item.id)}>
                        {t('checklist.restore')}
                      </button>
                      <button class="trash-delete" onclick={() => permanentlyDeleteFromTrash(item.id)} title={t('checklist.deletePerm')}>
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                  {/if}
                </div>

                {#if hasChildren && isExpanded}
                  <div class="tree-branch">
                    {#each children as child (child.id)}
                      {@render TrashItem(child, depth + 1)}
                    {/each}
                  </div>
                {/if}
              </div>
            {/snippet}

            {#each archivedRoots as root (root.id)}
              {@render TrashItem(root, 0)}
            {/each}
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#snippet TreeItem(item: Node, depth: number = 0)}
    {@const isExpanded = expanded.has(item.id)}
    {@const children = tree.get(item.id) ?? []}
    {@const hasChildren = children.length > 0}
    {@const isSelected = localContext?.nodeId === item.id || (localContext?.path && localContext.path.includes(item.id))}
    {@const levelLabel = item.type.charAt(0).toUpperCase() + item.type.slice(1)}
    {@const iconName = item.type === 'subject' ? 'book' : item.type === 'subtopic' ? 'layers' : item.type === 'book' ? 'book-open' : item.type === 'chapter' ? 'hash' : 'file-text'}

    <div class="tree-node">
      <div class="tree-item-row" class:selected={isSelected} role="button" tabindex="0" onclick={() => selectTopic(item)} onkeydown={(e) => e.key === 'Enter' && selectTopic(item)}>
        {#if hasChildren}
          <button class="chevron-btn" onclick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}>
            <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size={14} />
          </button>
        {:else if depth > 0}
          <span class="chevron-spacer"></span>
        {/if}
        
        <span class="level-badge">{levelLabel}</span>
        <Icon name={iconName} size={14} class="node-icon" />
        <span class="item-title">{item.title}</span>
        
        <div class="tree-actions">
          <button title="Add {getNextLevel(item.type)}" class="add-btn" onclick={(e) => { e.stopPropagation(); startAdding(item); }}><Icon name="plus" size={14} /></button>
          <button title="Delete" onclick={(e) => { e.stopPropagation(); requestDeleteNode(item); }}><Icon name="trash" size={14} /></button>
        </div>
      </div>

      {#if addingChildTo?.id === item.id}
        <div class="add-child-input">
          <input bind:value={newChildName} placeholder="New {getNextLevel(item.type)}..." onkeydown={(e) => e.key === 'Enter' && saveChild(item)} />
          <button class="check-btn" onclick={() => saveChild(item)}><Icon name="check" size={14} /></button>
          <button class="cancel-btn" onclick={() => addingChildTo = null}><Icon name="x" size={14} /></button>
        </div>
      {/if}

      {#if isExpanded}
        <div class="children-container tree-branch">
          {#each children as child (child.id)}
            {@render TreeItem(child, depth + 1)}
          {/each}
          
        </div>
      {/if}
    </div>
  {/snippet}

</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    max-width: 1400px;
    width: 100%;
    margin-inline: auto;
    flex: 1;
    min-height: 0;
    /* Aseguramos que el contenedor principal se expanda */
    height: 100%;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
    flex-shrink: 0;
  }

  /* Ocultar el título duplicado en móvil */
  @media (max-width: 768px) {
    .view-head {
      display: none;
    }
  }

  .view-head-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  /* Sync badge: estado del sistema en el header (Nielsen #1, F6 §33).
     Sólo se muestra si la app tiene backend configurado. */
  .sync-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 24px;
    padding: 0 var(--space-2);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-full, 9999px);
    background: var(--color-surface);
    font-family: var(--font-body, sans-serif);
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
    cursor: help;
  }

  .sync-badge::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }

  .sync-badge[data-state='synced'] {
    color: var(--color-success);
    border-color: var(--color-success-tint);
  }

  .sync-badge[data-state='pending'] {
    color: var(--color-warning);
    border-color: var(--color-warning-tint);
  }

  .sync-badge[data-state='warning'] {
    color: var(--color-warning);
    border-color: var(--color-warning);
  }

  .sync-badge[data-state='error'] {
    color: var(--color-danger);
    border-color: var(--color-danger);
  }

  .view-subtitle {
    margin: 0;
    font-family: var(--font-body, var(--font-sans));
    font-size: 0.85rem;
    color: var(--color-text-secondary);
  }

  .card-title {
    margin: 0 0 var(--space-2) 0;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 1.125rem;
    letter-spacing: -0.01em;
    color: var(--color-text-primary);
  }


  /* Pomodoro Mockup Styles */
  .pomo-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }
  .timer-mode-toggle {
    display: flex;
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    border-radius: 6px;
    padding: 2px;
  }
  .timer-mode-toggle button {
    padding: 4px 8px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-muted);
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .timer-mode-toggle button.active {
    background: var(--color-border);
    color: var(--color-text-primary);
    box-shadow: 0 1px 2px rgba(0,0,0,0.2);
  }
  .pomo-center {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1; 
  }
  .pomo-presets {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin-bottom: 8px;
    width: 100%;
    transition: opacity 0.3s;
  }
  .pomo-presets.disabled {
    opacity: 0.3;
    pointer-events: none;
  }
  .preset-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    font-size: var(--text-xs, 12px);
    font-weight: 500;
    border-radius: 6px;
    border: 1px solid var(--color-border);
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.2s;
  }
  .preset-btn:hover {
    color: var(--color-text-primary);
    border-color: var(--color-text-muted);
  }
  .preset-btn.active {
    border-color: var(--color-brand);
    background: color-mix(in srgb, var(--color-brand) 10%, transparent);
    color: var(--color-brand);
  }
  .custom-inputs {
    width: 100%;
    max-width: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: 8px 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    margin: 0 auto 16px auto;
  }
  .custom-inputs label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }
  .custom-inputs input {
    width: 56px;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    color: var(--color-text-primary);
    text-align: center;
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    padding: 4px 0;
  }
  .custom-inputs input:focus {
    outline: none;
    border-color: var(--color-brand);
  }
  .timer-circle-wrapper {
    position: relative;
    /* Tamaño proporcional: mínimo 140px, ideal 22vw, máximo 220px */
    width: clamp(140px, 22vw, 220px);
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .timer-svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }
  .timer-track {
    fill: none;
    stroke: var(--color-border);
    stroke-width: 2.5;
  }
  .timer-progress {
    fill: none;
    stroke: var(--color-brand);
    stroke-width: 2.5;
    transition: stroke-dashoffset var(--dur-slow) var(--ease-out);
    stroke-linecap: round;
  }
  .timer-text {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 10;
  }
  .timer-value {
    font-family: var(--font-mono);
    font-size: clamp(2rem, 6vh, 3rem);
    font-weight: 700;
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
    line-height: 1;
  }
  .timer-label {
    font-size: 10px;
    color: var(--color-text-muted);
    margin-top: 6px;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    font-weight: 600;
  }
  .pomo-footer {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: auto; /* Empuja el botón de Start siempre al fondo */
    padding-bottom: var(--space-4);
  }
  .start-focus-btn {
    width: fit-content;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: var(--color-brand);
    color: var(--color-brand-fg);
    font-weight: 600;
    font-size: 0.95rem;
    padding: 10px 24px;
    border-radius: var(--radius-xl);
    border: none;
    cursor: pointer;
    transition: transform var(--dur-fast) var(--ease-out),
                box-shadow var(--dur-fast) var(--ease-out),
                background-color var(--dur-fast) var(--ease-out);
    box-shadow: 0 0 25px -5px color-mix(in srgb, var(--color-brand) 50%, transparent);
  }
  .start-focus-btn:hover:not(:disabled) {
    background: var(--color-brand-hover);
    transform: scale(1.02);
  }
  .start-focus-btn:active:not(:disabled) {
    transform: scale(0.98);
  }
  .start-focus-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
  .pomo-subtext {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 12px;
    text-align: center;
    font-weight: 500;
  }
  .pomo-subtext .text-white {
    color: var(--color-text-primary);
  }

  /* Hint inline debajo del botón Start cuando falta contexto (F10 §5.2,
     Nielsen #1). Complementa el tooltip de `ariaLabel` con feedback
     persistente para usuarios que no usan hover. */


  .top-setup-bar {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: 1.5rem;
    padding: var(--space-2) var(--space-4);
    box-shadow: var(--shadow-sm);
  }

  .top-setup-bar :global(.context-breadcrumb-wrapper) {
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
  }

  .setup-left-col,
  .setup-right-col {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .setup-left-col {
    order: 1;
  }
  .setup-right-col {
    order: 2;
  }
  .setup-recent-col {
    order: 3;
  }

  .mode-container {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: 1.5rem;
    padding: var(--space-3) var(--space-4);
    box-shadow: var(--shadow-sm);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    flex-shrink: 0 !important; /* Evita que el contenedor de modo se aplaste */
    margin-bottom: var(--space-4);
  }

  .mode-container-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    font-family: var(--font-display);
    color: var(--color-text-primary);
  }

  .mode-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-4);
  }

  .mode-card {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    cursor: pointer;
    text-align: left;
    transition: transform var(--dur-fast) var(--ease-out),
                border-color var(--dur-fast) var(--ease-out),
                background-color var(--dur-fast) var(--ease-out);
  }

  .mode-card.active {
    border-color: var(--color-brand);
    background: var(--color-brand-tint);
  }

  .mode-card :global(svg) {
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  .mode-card.active :global(svg) {
    color: var(--color-brand);
  }

  .mode-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mode-name {
    font-size: var(--text-base);
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .mode-card.active .mode-name {
    color: var(--color-brand);
  }

  .mode-desc {
    font-size: var(--text-xs);
    color: var(--color-text-muted);
    line-height: 1.4;
  }

  .setup-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-4);
    width: 100%;
    align-items: stretch;
    /* Sin altura fija. Dejamos que el contenido dicte el tamaño. */
  }

  /* En móvil, quitamos la altura fija y apilamos */
  @media (max-width: 1024px) {
    .setup-grid {
      grid-template-columns: 1fr;
      height: auto;
    }
    .setup-pomodoro {
      overflow: visible;
      height: auto;
    }
  }

  .setup-grid.has-recent {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr);
  }

  /* Cuando hay problemas recientes, forzamos explícitamente cada columna
     a su posición en el grid, para evitar que un item se desplace a una
     nueva fila por conflictos con `order` o auto-placement. */
  .setup-grid.has-recent .setup-left-col {
    grid-column: 1;
    grid-row: 1;
  }
  .setup-grid.has-recent .setup-right-col {
    grid-column: 2;
    grid-row: 1;
  }
  .setup-grid.has-recent .setup-recent-col {
    grid-column: 3;
    grid-row: 1;
  }



  .setup-right-col {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .setup-recent-col {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }


  .review-actions {
    justify-content: flex-end;
    padding-top: var(--space-2);
    border-top: var(--border-width) solid var(--color-border);
  }

  .setup-pomodoro {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    min-width: 0;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: 1.5rem;
    padding: var(--space-6);
    box-shadow: var(--shadow-sm);
    height: 100%;
    /* Forzamos un tamaño mínimo para que el reloj quepa bien. */
    min-height: 600px; 
    transition:
      border-color var(--dur-fast) var(--ease-out),
      transform var(--dur-fast) var(--ease-out),
      box-shadow var(--shadow-sm);
  }

  /* Zoom 200%: apilamos columnas cuando el viewport CSS cae por debajo
     del umbral. Usamos `em` para que el breakpoint reaccione al zoom
     del navegador (F8 §5.6, WCAG 1.4.4). */
  @media (max-width: 70em) {
    .setup-grid,
    .setup-grid.has-recent {
      grid-template-columns: 1fr;
      grid-auto-rows: auto;
    }
    .setup-grid.has-recent .setup-left-col,
    .setup-grid.has-recent .setup-right-col,
    .setup-grid.has-recent .setup-recent-col {
      grid-column: 1;
      grid-row: auto;
    }
  }

  .setup-pomodoro:hover {
    border-color: var(--color-border-strong);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  .setup-pomodoro,
  .setup-checklist {
    display: flex;
    flex-direction: column;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: 1.5rem;
    padding: var(--space-6);
    box-shadow: var(--shadow-sm);
  }

  .setup-pomodoro,
  .setup-checklist {
    display: flex;
    flex-direction: column;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: 1.5rem;
    padding: var(--space-6);
    box-shadow: var(--shadow-sm);
    flex: 1; /* Hace que ambas tarjetas midan exactamente lo mismo */
    min-height: 0;
  }

  .setup-pomodoro {
    gap: var(--space-6);
  }

  .setup-checklist {
    display: flex;
    flex-direction: column;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: 1.5rem;
    padding: var(--space-6);
    box-shadow: var(--shadow-sm);
    height: 100%;
    min-height: 600px;
    /* El techo para que no crezca infinitamente */
    max-height: calc(100vh - 200px); 
    overflow: hidden;
  }

  .checklist-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: var(--space-2);
    padding-left: 0;
    overflow-y: auto;
    min-height: 0;
  }

  /* Responsividad: En pantallas pequeñas, permite que el pomodoro y el syllabus fluyan verticalmente */
  @media (max-height: 800px) {
    .setup-pomodoro {
      gap: var(--space-4);
      padding: var(--space-4);
    }
    .timer-circle-wrapper {
      width: 160px;
      height: 160px;
    }
    .timer-value {
      font-size: 2.5rem;
    }
  }






  @media (max-width: 1024px) {
    .setup-grid {
      grid-template-columns: 1fr;
    }
  }

  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .view-subtitle {
    margin: 0;
    font-family: var(--font-body, var(--font-sans));
    font-size: 0.85rem;
    color: var(--color-text-secondary);
  }



  .warn {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }


  .setup-checklist {
    display: flex;
    flex-direction: column;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: 1.5rem;
    padding: var(--space-6);
    max-height: calc(100vh - 180px); /* Evita que crezca más allá de la pantalla */
    min-height: 500px;
    box-shadow: var(--shadow-sm);
    overflow: hidden;
  }

  .checklist-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-3);
  }

  .checklist-header-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    font-family: var(--font-display);
    color: var(--color-text-primary);
  }

  .expand-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    cursor: pointer;
    transition: transform var(--dur-fast) var(--ease-out),
                border-color var(--dur-fast) var(--ease-out),
                background-color var(--dur-fast) var(--ease-out);
    flex-shrink: 0;
  }

  .expand-btn:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-border-strong);
  }

  .expand-btn:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }



  .checklist-footer {
    margin-top: var(--space-3);
    padding-top: var(--space-3);
    border-top: var(--border-width) solid var(--color-border);
    display: flex;
    justify-content: flex-end;
  }

  .trash-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: var(--radius-sm);
    transition: color var(--dur-fast) var(--ease-out),
                background-color var(--dur-fast) var(--ease-out);
  }

  .trash-btn:hover {
    color: var(--color-danger);
    background: var(--color-danger-tint);
  }

  .trash-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: var(--z-modal, 50);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
  }

  .trash-modal {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--space-5);
    max-width: 480px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    box-shadow: var(--shadow-lg);
  }

  .trash-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .trash-modal-header h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .trash-close {
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: 4px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .trash-close:hover {
    background: var(--color-surface-sunken);
    color: var(--color-text-primary);
  }

  .trash-empty {
    text-align: center;
    color: var(--color-text-muted);
    font-size: 0.875rem;
    padding: var(--space-4);
    margin: 0;
  }

  .trash-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    overflow-y: auto;
  }

  /* Clases obsoletas eliminadas, ahora usa las del árbol principal */
  .trash-restore {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-accent);
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: var(--radius-sm);
  }
  .trash-restore:hover {
    background: var(--color-accent-tint);
  }


  .trash-restore {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-accent);
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: var(--radius-sm);
  }

  .trash-delete {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--color-danger);
    cursor: pointer;
    padding: 4px;
    border-radius: var(--radius-sm);
    transition: background 0.2s;
  }

  .trash-delete:hover {
    background: var(--color-danger-tint);
  }

  .trash-restore:hover {
    background: var(--color-accent-tint);
  }

  .trash-note {
    font-size: var(--text-xs);
    color: var(--color-text-muted);
    margin-bottom: var(--space-3);
    padding: var(--space-2);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-sm);
    font-style: italic;
  }

  .delete-warning-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    color: var(--color-text-secondary);
    font-size: var(--text-sm);
  }
  .delete-tree-preview {
    list-style: none;
    padding: var(--space-3);
    margin: 0;
    background: var(--color-surface-sunken);
    border-radius: var(--radius-sm);
    border: 1px dashed var(--color-border-strong);
  }
  .delete-tree-preview li {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .delete-tree-preview .child-indicator {
    color: var(--color-text-muted);
    font-style: italic;
    margin-top: var(--space-2);
    font-size: var(--text-xs);
  }
  .delete-note {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2);
    background: var(--color-info-tint);
    color: var(--color-info);
    border-radius: var(--radius-sm);
    font-size: var(--text-xs);
    margin: 0;
  }
  .trash-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
    margin-top: var(--space-3);
  }

  .search-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: 8px 12px;
    margin-bottom: var(--space-3);
  }
  
  .search-bar input {
    flex: 1;
    background: transparent;
    border: none;
    color: var(--color-text-primary);
    font-size: var(--text-sm);
  }
  .search-bar input:focus {
    outline: none;
  }

  .checklist-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: var(--space-2);
    padding-left: 0;
    overflow-y: auto;
    min-height: 0; /* Permite el scroll interno en Flexbox */
  }


  .tree-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    opacity: 1; /* Siempre visible para que el usuario sepa que puede añadir hijos */
    transition: opacity 0.2s;
  }

  .tree-actions .add-btn {
    color: var(--color-brand);
  }
  .tree-actions .add-btn:hover {
    background: var(--color-brand-tint) !important;
    border-radius: var(--radius-sm);
  }



  .tree-actions button {
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: 4px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tree-actions button:hover {
    background: var(--color-surface-sunken);
    color: var(--color-text-primary);
  }

  .add-child-input {
    display: flex;
    gap: 4px;
    padding: var(--space-2) var(--space-2) var(--space-2) 2rem; /* Indentado para que se vea que es un hijo */
    margin-left: 1rem;
  }
  
  .add-child-input input {
    flex: 1;
    padding: 4px 8px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-surface);
    color: var(--color-text-primary);
    font-size: var(--text-sm);
  }

  .add-child-input button {
    padding: 4px;
    border-radius: var(--radius-sm);
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted);
    transition: color var(--dur-instant) var(--ease-out);
  }
  .add-child-input .check-btn:hover { color: var(--color-success); }
  .add-child-input .cancel-btn:hover { color: var(--color-danger); }
  




  .mode-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-3);
  }

  .mode-card {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-4);
    background: var(--color-surface);
    border: 2px solid var(--color-border);
    border-radius: var(--radius-lg);
    cursor: pointer;
    text-align: left;
    transition: transform var(--dur-fast) var(--ease-out),
                border-color var(--dur-fast) var(--ease-out),
                background-color var(--dur-fast) var(--ease-out);
  }

  .mode-card.active {
    border-color: var(--color-accent);
    background: var(--color-accent-tint);
  }

  .mode-card .mode-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .mode-card .mode-name {
    font-weight: 600;
    font-size: var(--text-base);
    color: var(--color-text-primary);
  }


  .empty-checklist {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    text-align: center;
    color: var(--color-text-muted);
    padding: var(--space-6);
  }


  .view-subtitle {
    font-size: var(--text-xs);
    color: var(--color-text-muted);
    margin-top: 2px;
  }


  .tree-node { margin-top: 2px; }
  
  .tree-item-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-2) var(--space-2) 0; /* Eliminamos padding izquierdo para alinear */
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out);
  }
  .tree-item-row:hover {
    background: var(--color-surface-hover);
  }
  .tree-item-row.selected {
    background: var(--color-brand-tint);
    /* En lugar de un borde completo, usamos un acento a la izquierda */
    border-color: transparent;
    border-left: 3px solid var(--color-brand);
    padding-left: calc(var(--space-2) - 2px); /* Compensamos el grosor del borde */
  }
  .tree-item-row.selected .item-title {
    color: var(--color-brand);
    font-weight: 600;
  }

  .chevron-btn, .chevron-spacer {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .level-badge {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.05em;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    background: transparent;
    color: var(--color-text-muted);
    border: 1px solid var(--color-border);
  }
  .tree-item-row.selected .level-badge {
    background: transparent;
    color: var(--color-brand);
    border-color: var(--color-brand);
  }

  .item-title {
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tree-actions {
    display: flex;
    gap: var(--space-1);
    opacity: 0;
    transition: opacity var(--dur-fast) var(--ease-out);
  }
  .tree-item-row:hover .tree-actions {
    opacity: 1;
  }
  .tree-actions button {
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: 2px;
    border-radius: var(--radius-sm);
  }
  .tree-actions button:hover {
    color: var(--color-text-primary);
    background: var(--color-surface-sunken);
  }

  .tree-branch {
    position: relative;
    padding-left: 1.5rem;
    margin-left: 1rem; 
  }
  .tree-branch::before {
    content: '';
    position: absolute;
    left: 0.5rem;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: var(--color-border);
  }
  .tree-branch:last-child::before {
    bottom: 50%;
  }

  .add-ghost-btn {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2);
    margin-top: var(--space-1);
    border-radius: var(--radius-sm);
    border: 1px dashed var(--color-border-strong);
    color: var(--color-text-muted);
    background: transparent;
    cursor: pointer;
    font-size: var(--text-xs);
    font-weight: 500;
    width: 100%;
    transition: border-color var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
    animation: pulse-border 2.5s infinite;
  }
  .add-ghost-btn:hover {
    border-color: var(--color-brand);
    color: var(--color-brand);
    animation: none;
  }
  @keyframes pulse-border {
    0% { border-color: var(--color-border-strong); box-shadow: 0 0 0 transparent; }
    50% { border-color: var(--color-brand-tint); box-shadow: 0 0 8px var(--color-brand-tint); }
    100% { border-color: var(--color-border-strong); box-shadow: 0 0 0 transparent; }
  }

  .context-breadcrumb-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    width: 100%;
    margin-bottom: var(--space-4);
    padding: 0; /* Quitamos el fondo feo */
    background: transparent;
    border-radius: var(--radius-md);
    border: none;
  }

  /* Sobrescribimos el layout interno de SessionContextFields para hacerlo horizontal tipo chips */
  .context-breadcrumb-wrapper :global(.context-fields) {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--space-1);
    flex-wrap: wrap;
  }

  .context-breadcrumb-wrapper :global(.field) {
    flex-direction: row;
    align-items: center;
    gap: var(--space-2);
    width: auto;
    min-width: 120px;
    flex: 0 1 auto;
  }

  /* Ocultamos las etiquetas (Subject, Subtopic, etc.) para que solo queden las píldoras */
  .context-breadcrumb-wrapper :global(.mini-label) {
    display: none;
  }

  /* Añadimos la flecha separadora entre los selectores */
  .context-breadcrumb-wrapper :global(.field:not(:last-child))::after {
    content: '›';
    color: var(--color-text-muted);
    font-size: 1.2rem;
    margin-left: var(--space-1);
    font-weight: bold;
  }

  /* Ajustamos el input select para que se vea más como una píldora */
  .context-breadcrumb-wrapper :global(.field select), 
  .context-breadcrumb-wrapper :global(.field .select-trigger) {
    border-radius: var(--radius-full, 999px);
    background: var(--color-surface);
  }
  /* === TÍTULOS UNIFICADOS GLOBALES === */
  .view-title {
    font-family: var(--font-display) !important;
    font-size: 1.35rem !important;
    font-weight: 700 !important;
    letter-spacing: -0.02em !important;
    line-height: 1.1 !important;
    margin: 0 !important;
    color: var(--color-text-primary) !important;
  }
  .view-subtitle {
    font-family: var(--font-body, var(--font-sans)) !important;
    font-size: var(--text-sm) !important;
    color: var(--color-text-muted) !important;
    margin: 0 !important;
    margin-top: var(--space-1) !important;
  }
  .mode-container-title,
  .checklist-header-title,
  .card-title {
    font-size: 1.125rem !important;
    font-family: var(--font-display) !important;
    font-weight: 700 !important;
    margin: 0 !important;
    color: var(--color-text-primary) !important;
  }

  /* === LAYOUT: EQUAL HEIGHT CARDS WITH INTERNAL SCROLL === */
  .setup-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: var(--space-4) !important;
    flex: 1 !important;
    min-height: 0 !important;
    max-height: calc(100dvh - 130px) !important; 
    align-items: stretch !important;
    overflow: hidden !important;
  }

  /* Variante con 3 columnas cuando hay problemas recientes.
     Debe ganar sobre el `1fr 1fr !important` de arriba. */
  .setup-grid.has-recent {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) !important;
  }

  .setup-left-col,
  .setup-right-col,
  .setup-recent-col {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }

  .setup-grid.has-recent .setup-left-col {
    grid-column: 1 !important;
    grid-row: 1 !important;
  }
  .setup-grid.has-recent .setup-right-col {
    grid-column: 2 !important;
    grid-row: 1 !important;
  }
  .setup-grid.has-recent .setup-recent-col {
    grid-column: 3 !important;
    grid-row: 1 !important;
  }

  .setup-pomodoro,
  .setup-checklist {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 !important;
    height: 100% !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: hidden !important;
  }

  .setup-pomodoro {
    overflow-y: auto !important;
  }

  .checklist-list {
    flex: 1 !important;
    min-height: 0 !important;
    overflow-y: auto !important;
  }

  .pomo-header,
  .pomo-presets,
  .pomo-footer {
    flex-shrink: 0 !important;
  }

  .pomo-center {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    flex: 1 !important;
  }

  .timer-circle-wrapper {
    width: clamp(140px, 22vw, 220px) !important;
    aspect-ratio: 1 !important;
    flex-shrink: 0 !important;
  }

  @media (max-width: 1024px) {
    .setup-grid,
    .setup-grid.has-recent {
      grid-template-columns: 1fr !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }
    .setup-grid.has-recent .setup-left-col,
    .setup-grid.has-recent .setup-right-col,
    .setup-grid.has-recent .setup-recent-col {
      grid-column: 1 !important;
      grid-row: auto !important;
    }
    .setup-pomodoro,
    .setup-checklist,
    .setup-recent-col {
      height: auto !important;
      overflow: visible !important;
    }
    .checklist-list {
      max-height: 400px !important;
    }
  }
</style>